import { html, render } from 'lit-html';
import * as brandStyles from 'url:../../brand.css';
import * as picStyles from 'url:./style.css';

class PicCarousel extends HTMLElement {
  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this._active = 0;
    this._autoplayTimer = null;
    this._autoplayMs = 0;
    this._navLock = false;
  }

  static get observedAttributes() {
    return ['autoplay'];
  }

  get autoplay() {
    return this._autoplayMs;
  }

  set autoplay(v) {
    this._autoplayMs = this._parseAutoplay(v);
    this._restartAutoplay();
  }

  attributeChangedCallback(name, _old, val) {
    if (name === 'autoplay') {
      this._autoplayMs = this._parseAutoplay(val);
      this._restartAutoplay();
    }
  }

  connectedCallback() {
    this._autoplayMs = this._parseAutoplay(this.getAttribute('autoplay'));
    this.render();
    this._afterRender();
    // kick off after layout settles
    queueMicrotask(() => this._restartAutoplay());
  }

  disconnectedCallback() {
    this._stopAutoplay();
    this._io?.disconnect();
    this._ro?.disconnect();
  }

  _template() {
    return html`
      <link href="${brandStyles}" rel="stylesheet" />
      <link href="${picStyles}" rel="stylesheet" />
      <div class="pc-wrap">
        <button
          class="pc-btn pc-prev"
          type="button"
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button class="pc-btn pc-next" type="button" aria-label="Next slide">
          ›
        </button>

        <div class="pc-track" part="track">
          <slot id="slotImgs"></slot>
        </div>

        <div class="pc-fade" aria-hidden="true"></div>
        <div class="pc-dots" role="tablist" aria-label="Slide indicators"></div>
      </div>
    `;
  }

  render() {
    render(this._template(), this.shadow);
  }

  _afterRender() {
    this.$track = this.shadow.querySelector('.pc-track');
    this.$prev = this.shadow.querySelector('.pc-prev');
    this.$next = this.shadow.querySelector('.pc-next');
    this.$dots = this.shadow.querySelector('.pc-dots');
    this.$slot = this.shadow.querySelector('#slotImgs');

    const build = () => {
      const slides = this._slidesEls();
      // Build dots
      this.$dots.innerHTML = '';
      slides.forEach((_, i) => {
        const d = document.createElement('button');
        d.className = 'pc-dot';
        d.type = 'button';
        d.setAttribute('role', 'tab');
        d.setAttribute('aria-label', `Go to slide ${i + 1}`);
        d.addEventListener('click', () => this.goTo(i));
        this.$dots.appendChild(d);
      });
      // Observe visibility to update active index
      this._observeSlides();
      this._setActive(0, false);
    };

    this.$slot.addEventListener('slotchange', build);
    build();

    this.$prev.addEventListener('click', () => this.prev());
    this.$next.addEventListener('click', () => this.next());

    this.shadow.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });

    this._ro?.disconnect();
    this._ro = new ResizeObserver(() => this._snapToActive());
    this._ro.observe(this.$track);

    this._restartAutoplay();
  }

  _slidesEls() {
    // Slotted nodes are laid out as children of track for flex purposes
    const assigned = this.shadow
      .querySelector('#slotImgs')
      .assignedElements({ flatten: true });
    // Allow <img> and generic slide containers
    return assigned.filter(
      (el) => el.tagName === 'IMG' || el.classList.contains('pc-slide'),
    );
  }

  _isAutoLocked(idx = this._active) {
    const slides = this._slidesEls();
    const el = slides[idx];
    return el?.hasAttribute('data-lock-autoplay');
  }

  _observeSlides() {
    this._io?.disconnect();
    const slides = this._slidesEls();
    if (!slides.length) return;

    const opts = { root: this.$track, threshold: 0.6 };
    this._io = new IntersectionObserver((entries) => {
      if (this._navLock) return; // Don't re-pick active mid-jump
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) {
        const idx = this._slidesEls().indexOf(visible.target);
        if (idx !== -1) this._setActive(idx, false);
      }
    }, opts);
    slides.forEach((el) => this._io.observe(el));
  }

  _setActive(i, smooth = true) {
    const slides = this._slidesEls();
    if (!slides.length) return;

    const len = slides.length;
    const prev = this._active;
    const raw = i;
    const target = (i + len) % len;

    this._active = target;

    Array.from(this.$dots.children).forEach((d, k) => {
      d.setAttribute('aria-current', k === target ? 'true' : 'false');
    });
    slides.forEach((el, k) => {
      el.setAttribute('data-active', k === target ? 'true' : 'false');
      el.setAttribute('tabindex', k === target ? '0' : '-1');
    });

    if (this._isAutoLocked(target)) {
      this._stopAutoplay();
    }

    // Detect wrap (len-1 -> 0 or 0 -> len-1) OR multi-hop jump (distance > 1)
    const wrappedForward = prev === len - 1 && raw >= len;
    const wrappedBackward = prev === 0 && raw < 0;
    const isWrap = wrappedForward || wrappedBackward;

    const distCW = (target - prev + len) % len; // clockwise steps
    const distCCW = (prev - target + len) % len; // counter-clockwise steps
    const multiHop = Math.min(distCW, distCCW) > 1;

    if (isWrap || multiHop) {
      // Hard jump: disable snap + IO updates during programmatic scroll
      this._navLock = true;
      const track = this.$track;

      const prevSnap = track.style.scrollSnapType;
      const prevBeh = track.style.scrollBehavior;

      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';

      // Calculate scroll position within the track container only
      const trackRect = track.getBoundingClientRect();
      const slideRect = slides[target].getBoundingClientRect();
      const scrollLeft =
        track.scrollLeft +
        (slideRect.left - trackRect.left) -
        (trackRect.width - slideRect.width) / 2;
      track.scrollLeft = scrollLeft;

      requestAnimationFrame(() => {
        track.style.scrollSnapType = prevSnap || 'x mandatory';
        track.style.scrollBehavior = prevBeh || 'smooth';
        this._navLock = false;
      });
      return;
    }

    // Normal in-bounds move (single step) - scroll within track only
    const track = this.$track;
    const trackRect = track.getBoundingClientRect();
    const slideRect = slides[target].getBoundingClientRect();
    const scrollLeft =
      track.scrollLeft +
      (slideRect.left - trackRect.left) -
      (trackRect.width - slideRect.width) / 2;

    if (!smooth) track.style.scrollBehavior = 'auto';
    track.scrollLeft = scrollLeft;
    if (!smooth) queueMicrotask(() => (track.style.scrollBehavior = 'smooth'));
  }

  _parseAutoplay(val) {
    if (val == null) return 0;
    if (val === '' || val === 'true') return 8000;
    if (/^\d+\s*ms$/i.test(val)) return parseInt(val, 10);
    if (/^\d+(\.\d+)?\s*s$/i.test(val))
      return Math.round(parseFloat(val) * 1000);
    const n = Number(val);
    if (!Number.isFinite(n)) return 0;
    return n > 0 && n <= 50 ? n * 1000 : n;
  }

  _snapToActive() {
    this._setActive(this._active, false);
  }

  next() {
    this._setActive(this._active + 1);
    this._restartAutoplay(true);
  }
  prev() {
    this._setActive(this._active - 1);
    this._restartAutoplay(true);
  }
  goTo(i) {
    this._setActive(i);
    this._restartAutoplay(true);
  }

  _restartAutoplay(userNav = false) {
    this._stopAutoplay();
    if (this._isAutoLocked()) return;
    if (this._autoplayMs > 0) {
      const delay = userNav
        ? Math.min(this._autoplayMs, 4000)
        : this._autoplayMs;
      this._autoplayTimer = setTimeout(() => {
        this.next();
        this._restartAutoplay();
      }, delay);
    }
  }
  _stopAutoplay() {
    if (this._autoplayTimer) clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
  }
}
customElements.define('pic-carousel', PicCarousel);
